import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import FlightSearchWidget from "@/components/site/FlightSearchWidget";
import FlightStepper from "@/components/site/FlightStepper";
import FlightLoaderModal from "@/components/site/FlightLoaderModal";
import PromoStrip from "@/components/site/PromoStrip";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Plane, Clock, ArrowRight, Briefcase, Utensils, Armchair, RefreshCw, Check, ShieldCheck, AlertCircle, SearchX, SlidersHorizontal, Database, ChevronDown, ChevronUp, MapPin, FileText } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { useAirlineLogos, airlineLogoUrl } from "@/lib/airlineLogos";
import { toast } from "sonner";
import { draftStore } from "@/lib/flightDraft";
import { formatCheckedBaggage } from "@/lib/bagFormat";
import FareBreakdown from "@/components/site/FareBreakdown";
import FlightSegmentTimeline from "@/components/site/FlightSegmentTimeline";
import FareRulesDialog from "@/components/site/FareRulesDialog";

function formatDur(min) {
  const h = Math.floor(min / 60), m = min % 60;
  return `${h}h ${m}m`;
}

const DEFAULT_DATE = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

const SORT_OPTIONS = [
  { id: "cheapest", label: "Cheapest" },
  { id: "fastest", label: "Fastest" },
  { id: "earliest", label: "Earliest" },
];

export default function Flights() {
  const { state } = useLocation();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selections, setSelections] = useState({}); // segIndex -> {flight, fare}
  // Collapsed-by-default search header once results have arrived, so the
  // flight cards get the focus. User can click "Edit search" to expand.
  const [searchOpen, setSearchOpen] = useState(false);
  // Flexible-dates ± 3 days price strip — fetched from /api/flights/flex-search
  // when the user toggled "Flexible dates" on the search widget.
  const [flexStrip, setFlexStrip] = useState(null);
  const [flexLoading, setFlexLoading] = useState(false);
  // Sequential segment selection — show one segment at a time on round-trip / multi-city.
  // The user picks a flight in segment 0, it collapses into a summary, then segment 1 reveals.
  const [activeSegIndex, setActiveSegIndex] = useState(0);
  const [activeFlight, setActiveFlight] = useState(null); // {segIndex, flight}
  const [fareRulesFor, setFareRulesFor] = useState(null); // {flight, fareRules, label}
  // Cross-leg carrier-combinable guard — once leg 0 is picked, default to
  // showing only matching-carrier flights on later legs (highest fare-combine
  // success rate). User can toggle off to widen the search.
  const [carrierGuard, setCarrierGuard] = useState(true);
  const { user } = useAuth();
  const { format: formatCcy, currency: dispCcy } = useCurrency();
  const airlineLogos = useAirlineLogos();
  const navigate = useNavigate();

  // Filter state
  const [sortBy, setSortBy] = useState("cheapest");
  const [priceRange, setPriceRange] = useState(null); // [min, max]
  const [stopFilters, setStopFilters] = useState({ 0: true, 1: true, "2+": true });
  const [airlineFilters, setAirlineFilters] = useState({}); // code -> bool
  const [maxDuration, setMaxDuration] = useState(null);

  // computed once at module import time so the params memo is stable
  const params = useMemo(() => state || { trip_type: "one_way", passengers: 1, cabin: "economy", segments: [{ from: "CAI", to: "JED", date: DEFAULT_DATE }] }, [state]);
  const paramsKey = useMemo(() => JSON.stringify(params), [params]);
  useEffect(() => {
    let active = true;
    const reset = () => {
      setSelections({});
      setActiveSegIndex(0);
      setCarrierGuard(true);
      setPriceRange(null);
      setStopFilters({ 0: true, 1: true, "2+": true });
      setAirlineFilters({});
      setMaxDuration(null);
    };
    const fetchIt = async () => {
      reset();
      setLoading(true);
      setError(null);
      setSearchOpen(false); // collapse the search header when a new query starts
      try {
        const { data } = await api.post("/flights/search", params);
        if (active) {
          setResults(data);
          // Auto-scroll just enough to bring the results into focus — keeps the
          // collapsed search ribbon visible at the top, the flight cards centred.
          requestAnimationFrame(() => {
            const el = document.querySelector('[data-testid="flight-results-anchor"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Search failed. Please try again.";
        if (active) { setError(msg); setResults(null); toast.error(msg); }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchIt();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // ---- Flex-dates strip: fetched once per search when params.flex_dates is on
  useEffect(() => {
    let active = true;
    if (!params?.flex_dates) { setFlexStrip(null); return; }
    setFlexLoading(true);
    (async () => {
      try {
        const { data } = await api.post("/flights/flex-search", params);
        if (active) setFlexStrip(data);
      } catch { if (active) setFlexStrip(null); }
      finally { if (active) setFlexLoading(false); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // ---- Derived: airline list, price/duration bounds (across all segments) ----
  const { allAirlines, priceBounds, durationBounds } = useMemo(() => {
    const airlines = new Map();
    let pMin = Infinity, pMax = -Infinity, dMin = Infinity, dMax = -Infinity;
    (results?.results || []).forEach((seg) => {
      (seg.flights || []).forEach((f) => {
        if (!airlines.has(f.airline_code)) airlines.set(f.airline_code, f.airline_name);
        pMin = Math.min(pMin, f.price);
        pMax = Math.max(pMax, f.price);
        dMin = Math.min(dMin, f.duration_min);
        dMax = Math.max(dMax, f.duration_min);
      });
    });
    if (!isFinite(pMin)) { pMin = 0; pMax = 0; dMin = 0; dMax = 0; }
    return {
      allAirlines: Array.from(airlines.entries()).sort(),
      priceBounds: [Math.floor(pMin), Math.ceil(pMax)],
      durationBounds: [dMin, dMax],
    };
  }, [results]);

  const activePriceRange = priceRange || priceBounds;
  const activeMaxDuration = maxDuration ?? durationBounds[1];

  const applyFilters = (flights) => {
    let out = flights.filter((f) => {
      if (f.price < activePriceRange[0] || f.price > activePriceRange[1]) return false;
      const stopKey = f.stops === 0 ? 0 : (f.stops === 1 ? 1 : "2+");
      if (!stopFilters[stopKey]) return false;
      const airlineOn = airlineFilters[f.airline_code];
      // If at least one airline checked, only those flights show; if none checked, show all
      const anyAirlineSelected = Object.values(airlineFilters).some(Boolean);
      if (anyAirlineSelected && !airlineOn) return false;
      if (f.duration_min > activeMaxDuration) return false;
      return true;
    });
    if (sortBy === "cheapest") out = [...out].sort((a, b) => a.price - b.price);
    else if (sortBy === "fastest") out = [...out].sort((a, b) => a.duration_min - b.duration_min);
    else if (sortBy === "earliest") out = [...out].sort((a, b) => a.depart_time.localeCompare(b.depart_time));
    return out;
  };

  const segCount = results?.results?.length || 0;
  const allSelected = segCount > 0 && Object.keys(selections).length === segCount;
  const pax = Number(params.passengers || 1);
  const total = Object.values(selections).reduce(
    (s, sel) => s + ((sel.flight?.price || 0) + (sel.fare?.price_delta || 0)), 0
  ) * pax;

  const totalAvailable = (results?.results || []).reduce((s, seg) => s + (seg.flights?.length || 0), 0);

  const openFare = async (segIndex, flight) => {
    setActiveFlight({ segIndex, flight, upsellFares: null, upsellLoading: true, upsellReason: null });
    // Travelport path: call AirPriceReq with FaresIndicator="AllFares"
    if (flight.supplier_source === "travelport") {
      try {
        const { data } = await api.post("/travelport/air-price", {
          flight, pax_count: Number(params.passengers || 1),
        });
        const brands = (data.brands || []).map((b) => ({
          code: b.code,
          name: b.name || b.code,
          brand_tier: b.brand_tier,
          rbd: b.rbd,
          cabin: b.cabin,
          fare_basis: b.fare_basis,
          price: b.total_price,
          price_delta: b.price_delta || 0,
          base_price: b.base_price,
          taxes: b.taxes,
          cabin_baggage_kg: b.cabin_baggage_kg || 7,
          checked_baggage_kg: b.checked_baggage_kg || 0,
          checked_baggage_qty: b.checked_baggage_qty || 0,
          meal: !!b.meal, seat_selection: !!b.seat_selection,
          refundable: !!b.refundable, changeable_fee: b.changeable_fee || 75,
          fare_rules: b.fare_rules || null,
        }));
        setActiveFlight((cur) => cur && cur.flight.id === flight.id
          ? { ...cur, upsellFares: brands, upsellLoading: false,
              upsellAvailable: brands.length > 0,
              upsellReason: brands.length === 0 ? "No alternative fares returned for this route." : null }
          : cur);
      } catch (e) {
        setActiveFlight((cur) => cur && cur.flight.id === flight.id
          ? { ...cur, upsellFares: flight.brand_fares || [], upsellLoading: false,
              upsellAvailable: false, upsellReason: "AirPrice service unreachable" }
          : cur);
      }
      return;
    }
    // Amadeus path
    try {
      const { data } = await api.post("/flights/upsell", {
        flight_offer: flight.amadeus_offer,
        travelers: [{ id: "1", travelerType: "ADULT" }],
      });
      setActiveFlight((cur) => cur && cur.flight.id === flight.id
        ? { ...cur, upsellFares: data.fares || [], upsellLoading: false, upsellAvailable: !!data.available, upsellReason: data.reason || null }
        : cur);
    } catch (_e) {
      setActiveFlight((cur) => cur && cur.flight.id === flight.id ? { ...cur, upsellFares: [], upsellLoading: false, upsellAvailable: false, upsellReason: "Upsell service unreachable" } : cur);
    }
  };

  const confirmFare = (fare) => {
    const segIndex = activeFlight.segIndex;
    setSelections({ ...selections, [segIndex]: { flight: activeFlight.flight, fare } });
    setActiveFlight(null);
    // For round-trip / multi-city: advance to the next segment so its results
    // open while previous segments collapse to their chosen-summary cards.
    // When the LAST segment is picked, advance one past it so the just-picked
    // leg also satisfies `idx < activeSegIndex` and collapses.
    const segCount = (results?.results || []).length;
    if (segCount > 1) {
      const nextIndex = Math.min(segIndex + 1, segCount);
      setActiveSegIndex(nextIndex);
      if (nextIndex < segCount) {
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-testid="flight-segment-heading-${nextIndex}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  };

  const changeSegment = (segIndex) => {
    // Re-open this segment's results. Drop the existing selection for that leg
    // (and any later leg, since their pricing may depend on the upstream pick).
    setSelections((cur) => {
      const next = { ...cur };
      Object.keys(next).forEach((k) => { if (Number(k) >= segIndex) delete next[k]; });
      return next;
    });
    setActiveSegIndex(segIndex);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="flight-segment-heading-${segIndex}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const proceed = () => {
    if (!user) { toast.error("Please sign in to continue"); return; }
    const draft = {
      search: params,
      selections: Object.values(selections).map((s) => ({ flight: s.flight, fare: s.fare })),
      created_at: new Date().toISOString(),
    };
    draftStore.set(draft);
    navigate("/flights/travelers");
  };

  const resetFilters = () => {
    setPriceRange(null);
    setStopFilters({ 0: true, 1: true, "2+": true });
    setAirlineFilters({});
    setMaxDuration(null);
    setSortBy("cheapest");
  };

  const hasResults = totalAvailable > 0;
  const [expanded, setExpanded] = useState({}); // flightId -> bool

  // Client-side airline + direct filters from search payload
  const airlineFilter = (params?.airline || "").toUpperCase();
  const directOnly = !!params?.direct;
  const applySearchFilters = (flights) => flights.filter((f) => {
    if (airlineFilter && (f.airline_code || "").toUpperCase() !== airlineFilter) return false;
    if (directOnly && f.stops > 0) return false;
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Modal loader overlays everything while in-flight */}
      {loading && (
        <FlightLoaderModal
          segments={params?.segments || []}
          pax={params?.passengers || 1}
        />
      )}

      <div data-testid="flight-search-header" className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-5 md:p-6">
        {/* COLLAPSED ribbon — two rows: stepper on top, trip summary + edit below.
            Keeps the layout breathable on round-trip / multi-city without
            collapsing the stepper pills into a single squished row. */}
        {!searchOpen && results && !loading ? (
          <div className="space-y-4">
            <div className="-mx-1">
              <FlightStepper current="select" />
            </div>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div
                className="text-white font-mono text-sm sm:text-base flex items-center gap-2 min-w-0 overflow-x-auto whitespace-nowrap flex-1 [&::-webkit-scrollbar]:hidden"
                data-testid="flight-search-summary"
              >
                {(params?.segments || []).map((seg, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5">
                    {i > 0 && <span className="text-white/40">·</span>}
                    <span className="font-bold">{seg.from}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-white/70" />
                    <span className="font-bold">{seg.to}</span>
                    {seg.date && <span className="text-white/60 text-xs">{seg.date}</span>}
                  </span>
                ))}
                <span className="text-white/40">·</span>
                <span className="text-white/80 text-xs font-sans">
                  {params?.passengers || 1} pax · {params?.cabin || "economy"}
                </span>
              </div>
              <Button
                type="button"
                data-testid="flight-edit-search"
                onClick={() => setSearchOpen(true)}
                className="rounded-full bg-white text-[var(--brand-accent)] hover:bg-slate-100 font-medium px-5 shrink-0"
              >
                Edit search
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h1 className="text-white font-heading text-2xl sm:text-3xl font-semibold">Find your perfect flight</h1>
              <FlightStepper current="select" />
            </div>
            <div className="mt-6"><FlightSearchWidget initial={params} compact /></div>
          </>
        )}
      </div>

      {/* Anchor used by the post-search auto-scroll so the results take focus
          while the collapsed ribbon stays pinned at the top of the viewport. */}
      <div data-testid="flight-results-anchor" aria-hidden="true" style={{ scrollMarginTop: 160 }} />

      {/* Flexible-dates ± 3 day price strip — clicking a cell re-runs the
          search with the shifted outbound date. Only rendered when the user
          toggled "Flexible dates" on the home / edit search widget. */}
      {params?.flex_dates && (flexStrip || flexLoading) && (
        <div className="mt-4" data-testid="flex-date-strip">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--brand-primary)] font-bold">
              Flexible dates — ± 3 days
            </div>
            {flexLoading && <span className="text-[11px] text-slate-500 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> comparing prices…</span>}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {(flexStrip?.prices || Array.from({length:7})).map((p, i) => {
              const isLoading = !p;
              const isCenter = p?.delta_days === 0;
              const cheapest = flexStrip?.prices?.filter(x => x.price != null)?.reduce((m, x) => (m == null || x.price < m ? x.price : m), null);
              const isCheapest = p?.price != null && p.price === cheapest;
              const clickable = p?.price != null && !isCenter;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!clickable}
                  data-testid={`flex-date-cell-${p?.delta_days ?? i}`}
                  onClick={() => {
                    if (!clickable) return;
                    // Rewrite the outbound date and (for round-trip) preserve
                    // the original return offset.
                    const next = JSON.parse(JSON.stringify(params));
                    const oldOut = next.segments[0].date;
                    next.segments[0].date = p.date;
                    if (next.trip_type === "round_trip" && next.segments[1]?.date) {
                      const od = new Date(oldOut), rd = new Date(next.segments[1].date);
                      const offset = Math.round((rd - od) / 86400000);
                      const nd = new Date(p.date); nd.setUTCDate(nd.getUTCDate() + offset);
                      next.segments[1].date = nd.toISOString().slice(0,10);
                    }
                    navigate("/flights", { state: next, replace: true });
                  }}
                  className={
                    "rounded-xl border px-3 py-2 text-left transition-all " +
                    (isLoading ? "border-slate-100 bg-slate-50 animate-pulse h-[68px]" :
                     isCenter ? "border-[var(--brand-primary)] bg-teal-50 cursor-default" :
                     clickable ? "border-slate-200 hover:border-[var(--brand-primary)] hover:bg-teal-50/40" :
                     "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed")
                  }
                  title={isCenter ? "Selected" : (p?.price == null ? "No flights" : `Switch to ${p.date}`)}
                >
                  {p && (
                    <>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                        {new Date(p.date).toLocaleDateString(undefined, { weekday: "short" })}
                      </div>
                      <div className="text-xs font-mono text-slate-700">
                        {new Date(p.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </div>
                      <div className={"text-sm font-bold mt-0.5 " +
                        (isCheapest ? "text-emerald-700" : "text-[var(--brand-accent)]")}>
                        {p.price != null ? formatCcy(p.price, { decimals: 0 }) : "—"}
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8">
        {!loading && error && (
          <Card data-testid="flight-error" className="p-8 text-center border-red-200 bg-red-50/40">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
            <div className="mt-3 font-heading text-lg font-semibold text-red-700">Search failed</div>
            <div className="text-sm text-slate-600 mt-1 max-w-xl mx-auto">{error}</div>
            <Button data-testid="flight-error-retry" onClick={() => navigate("/")} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
              Try a different search
            </Button>
          </Card>
        )}

        {!loading && !error && results && !hasResults && (
          <Card data-testid="flight-empty" className="p-8 text-center">
            <SearchX className="w-10 h-10 text-slate-400 mx-auto" />
            <div className="mt-3 font-heading text-lg font-semibold text-[var(--brand-accent)]">No flights found</div>
            <div className="text-sm text-slate-500 mt-1 max-w-xl mx-auto">
              We couldn&apos;t find any flights for your search. Try different dates or nearby airports (sandbox routes are limited — e.g. CAI → JED works well).
            </div>
            <Button data-testid="flight-empty-retry" onClick={() => navigate("/")} className="mt-4 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
              Modify search
            </Button>
          </Card>
        )}

        {!loading && !error && hasResults && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Filters Sidebar */}
            <aside data-testid="flight-filters" className="lg:col-span-3">
              <Card className="p-5 sticky top-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-heading text-lg font-semibold text-[var(--brand-accent)] flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4" /> Filters
                  </div>
                  <button data-testid="flight-filters-reset" onClick={resetFilters} className="text-xs text-[var(--brand-primary)] hover:underline">Reset</button>
                </div>

                {/* Sort */}
                <div className="mb-5">
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">Sort by</Label>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {SORT_OPTIONS.map((s) => (
                      <button key={s.id} data-testid={`flight-sort-${s.id}`} onClick={() => setSortBy(s.id)}
                              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${sortBy === s.id ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-slate-600 border-slate-200 hover:border-[var(--brand-primary)]"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price */}
                {priceBounds[1] > priceBounds[0] && (
                  <div className="mb-5">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Price ({dispCcy})</Label>
                    <div className="text-sm text-slate-700 mt-1">{formatCcy(activePriceRange[0], { decimals: 0 })} – {formatCcy(activePriceRange[1], { decimals: 0 })}</div>
                    <Slider data-testid="flight-filter-price"
                            min={priceBounds[0]} max={priceBounds[1]} step={1}
                            value={activePriceRange}
                            onValueChange={(v) => setPriceRange(v)}
                            className="mt-2" />
                  </div>
                )}

                {/* Stops */}
                <div className="mb-5">
                  <Label className="text-xs text-slate-500 uppercase tracking-wider">Stops</Label>
                  <div className="mt-2 space-y-2">
                    {[{ k: 0, l: "Non-stop" }, { k: 1, l: "1 stop" }, { k: "2+", l: "2+ stops" }].map((o) => (
                      <label key={o.k} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <Checkbox data-testid={`flight-filter-stops-${o.k}`}
                                  checked={!!stopFilters[o.k]}
                                  onCheckedChange={(c) => setStopFilters({ ...stopFilters, [o.k]: !!c })} />
                        {o.l}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                {durationBounds[1] > durationBounds[0] && (
                  <div className="mb-5">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Max duration</Label>
                    <div className="text-sm text-slate-700 mt-1">{formatDur(activeMaxDuration)}</div>
                    <Slider data-testid="flight-filter-duration"
                            min={durationBounds[0]} max={durationBounds[1]} step={5}
                            value={[activeMaxDuration]}
                            onValueChange={(v) => setMaxDuration(v[0])}
                            className="mt-2" />
                  </div>
                )}

                {/* Airlines */}
                {allAirlines.length > 1 && (
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Airlines</Label>
                    <div className="mt-2 space-y-2 max-h-48 overflow-auto pr-1">
                      {allAirlines.map(([code, name]) => (
                        <label key={code} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                          <Checkbox data-testid={`flight-filter-airline-${code}`}
                                    checked={!!airlineFilters[code]}
                                    onCheckedChange={(c) => setAirlineFilters({ ...airlineFilters, [code]: !!c })} />
                          <span className="font-mono text-xs text-slate-500 w-8">{code}</span>
                          <span className="truncate">{name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </aside>

            {/* Results */}
            <div className="lg:col-span-9">
              {/* Sticky Trip-Total pill — appears once at least one leg is picked
                  on a multi-leg search. Gives constant price reassurance as the
                  user advances through Segment 1 → 2 → 3. */}
              {segCount > 1 && Object.keys(selections).length > 0 && !allSelected && (
                <div
                  data-testid="trip-total-pill"
                  className="sticky top-3 z-30 mb-4 flex items-center gap-3 bg-white/95 backdrop-blur border border-[var(--brand-primary)]/30 rounded-full pl-4 pr-2 py-2 shadow-md w-fit ml-auto"
                >
                  <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-primary)] font-bold">Trip total so far</span>
                  <span className="font-heading text-lg font-bold text-[var(--brand-accent)]" data-testid="trip-total-pill-amount">
                    {formatCcy(total, { decimals: 0 })}
                  </span>
                  <span className="text-[11px] bg-teal-50 text-[var(--brand-primary)] font-semibold px-2 py-0.5 rounded-full">
                    {Object.keys(selections).length} of {segCount} legs picked
                  </span>
                </div>
              )}
              {results.results.map((seg, idx) => {
                let filtered = applyFilters(applySearchFilters(seg.flights || []));
                // Cross-leg carrier-combinable guard — for any segment after
                // the first one, filter down to flights whose plating carrier
                // matches the carrier picked on Segment 1. This drastically
                // improves the chance the GDS price re-quote at checkout
                // succeeds with a single combinable fare construction.
                const guardingCarrier = carrierGuard && idx > 0 && selections[0]?.flight?.airline_code;
                const guardedAway = guardingCarrier ? filtered.filter(f => f.airline_code !== selections[0].flight.airline_code).length : 0;
                if (guardingCarrier) {
                  const same = filtered.filter(f => f.airline_code === selections[0].flight.airline_code);
                  if (same.length > 0) filtered = same;
                }
                const sel = selections[idx];
                const isMultiSeg = results.results.length > 1;
                const isChosen = !!sel && isMultiSeg && idx < activeSegIndex;
                const isLocked = isMultiSeg && idx > activeSegIndex;
                return (
                  <div key={idx} className="mb-8">
                    <h2
                      data-testid={`flight-segment-heading-${idx}`}
                      className="font-heading text-xl font-semibold text-[var(--brand-accent)] mb-3 flex items-center gap-2 flex-wrap"
                    >
                      <span className={"text-xs font-bold px-2 py-0.5 rounded " + (isChosen ? "bg-emerald-50 text-emerald-700" : isLocked ? "bg-slate-100 text-slate-500" : "bg-teal-50 text-[var(--brand-primary)]")}>
                        Segment {idx + 1}{isChosen ? " ✓" : ""}
                      </span>
                      <span className="font-mono">{seg.from}</span> <ArrowRight className="w-4 h-4" /> <span className="font-mono">{seg.to}</span>
                      <span className="text-slate-500 text-base font-normal">· {seg.date}</span>
                      {!isChosen && !isLocked && (
                        <span className="ml-auto text-xs text-slate-400">{filtered.length} of {seg.flights.length} flights</span>
                      )}
                    </h2>

                    {/* COLLAPSED — chosen summary for a completed segment */}
                    {isChosen && (
                      <Card
                        data-testid={`flight-segment-chosen-${idx}`}
                        className="p-4 border-emerald-200 bg-gradient-to-r from-emerald-50/70 to-white flex items-center gap-4 flex-wrap"
                      >
                        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {airlineLogoUrl(airlineLogos, sel.flight.airline_code)
                            ? <img src={airlineLogoUrl(airlineLogos, sel.flight.airline_code)} alt={sel.flight.airline_code} className="w-full h-full object-contain p-1" />
                            : <span className="text-xs font-bold text-[var(--brand-primary)]">{sel.flight.airline_code}</span>}
                        </div>
                        <div className="text-sm">
                          <div className="font-semibold text-[var(--brand-accent)]">
                            {sel.flight.airline_name} · <span className="font-mono">{sel.flight.flight_number}</span>
                          </div>
                          <div className="text-xs text-slate-600">
                            <span className="font-mono">{sel.flight.depart_time}</span> {sel.flight.from} → <span className="font-mono">{sel.flight.arrive_time}</span> {sel.flight.to}
                            <span className="text-slate-400"> · {formatDur(sel.flight.duration_min)}</span>
                            <span className="text-slate-400"> · {sel.flight.stops === 0 ? "Non-stop" : `${sel.flight.stops} stop${sel.flight.stops>1?"s":""}`}</span>
                          </div>
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                          <div className="text-right">
                            <div className="font-heading text-xl font-bold text-[var(--brand-accent)]">{formatCcy(sel.flight.price + (sel.fare?.price_delta || 0), { decimals: 0 })}</div>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{sel.fare?.name || "Selected"}</div>
                          </div>
                          <Button
                            data-testid={`flight-change-segment-${idx}`}
                            variant="outline"
                            size="sm"
                            onClick={() => changeSegment(idx)}
                            className="rounded-full border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-teal-50"
                          >
                            Change
                          </Button>
                        </div>
                      </Card>
                    )}

                    {/* LOCKED — placeholder for segments not yet reachable */}
                    {isLocked && (
                      <Card data-testid={`flight-segment-locked-${idx}`} className="p-4 border-dashed border-slate-200 bg-slate-50/60 text-sm text-slate-500 flex items-center gap-2">
                        <Plane className="w-4 h-4 text-slate-400" />
                        Pick your Segment {activeSegIndex + 1} flight to unlock this leg.
                      </Card>
                    )}

                    {/* ACTIVE — full results list */}
                    {!isChosen && !isLocked && (
                      <>
                        {/* Carrier-combinable banner — visible on segments after seg 0
                            when the guard is filtering out flights from other carriers. */}
                        {guardingCarrier && guardedAway > 0 && (
                          <div
                            data-testid={`flight-carrier-guard-${idx}`}
                            className="mb-3 flex items-center gap-2 flex-wrap text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-lg"
                          >
                            <span className="font-semibold">Showing {selections[0].flight.airline_code} flights only</span>
                            <span className="text-amber-800/80">— same carrier across legs gives the best chance of a single combinable fare at checkout.</span>
                            <button
                              type="button"
                              data-testid={`flight-carrier-guard-disable-${idx}`}
                              onClick={() => setCarrierGuard(false)}
                              className="ml-auto text-amber-900 underline font-medium hover:text-amber-700"
                            >
                              Show all {guardedAway} other carriers
                            </button>
                          </div>
                        )}
                        {filtered.length === 0 ? (
                      <Card data-testid={`flight-segment-empty-${idx}`} className="p-6 text-center text-sm text-slate-500 border-dashed">
                        No flights match the current filters. <button onClick={resetFilters} className="text-[var(--brand-primary)] underline ml-1">Reset filters</button>
                      </Card>
                    ) : (
                      <div className="space-y-3">
                        {filtered.map((f) => {
                          const sel = selections[idx];
                          const isSel = sel?.flight?.id === f.id;
                          return (
                            <Card key={f.id} data-testid={`flight-card-${f.id}`}
                                  className={`p-5 transition-all ${isSel ? "ring-2 ring-[var(--brand-primary)] bg-teal-50/40" : "hover:shadow-md"}`}>
                              <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 cursor-pointer"
                                   onClick={() => setExpanded((e) => ({ ...e, [f.id]: !e[f.id] }))}>
                                <div className="md:col-span-3 flex items-center gap-3">
                                  <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[var(--brand-primary)] font-bold overflow-hidden">
                                    {airlineLogoUrl(airlineLogos, f.airline_code)
                                      ? <img src={airlineLogoUrl(airlineLogos, f.airline_code)} alt={f.airline_code}
                                             data-testid={`airline-logo-${f.airline_code}-${f.id}`}
                                             className="w-full h-full object-contain p-1" />
                                      : <span className="text-sm">{f.airline_code}</span>}
                                  </div>
                                  <div>
                                    <div className="font-medium text-[var(--brand-accent)]">{f.airline_name}</div>
                                    <div className="text-xs text-slate-500">{f.flight_number} · {f.aircraft}</div>
                                    {/* Supplier badge — visible to admin users only (for reference).
                                        Hidden from regular B2C customers. */}
                                    {user?.role === "admin" && (f.source_label || f.supplier_source) && (
                                      <div
                                        data-testid={`flight-supplier-${(f.supplier_source||f.source||"").toLowerCase()}-${f.id}`}
                                        className={
                                          "inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border " +
                                          ((f.supplier_source || f.source || "").toLowerCase() === "travelport"
                                            ? "text-violet-800 bg-violet-50 border-violet-200"
                                            : (f.supplier_source || f.source || "").toUpperCase() === "NDCX"
                                              ? "text-amber-800 bg-amber-50 border-amber-200"
                                              : "text-sky-800 bg-sky-50 border-sky-200")
                                        }
                                        title="Admin-only: source supplier"
                                      >
                                        <Database className="w-2.5 h-2.5" /> {f.source_label || f.supplier_source}
                                        {typeof f.markup_applied === "number" && f.markup_applied > 0 && (
                                          <span className="ml-1 normal-case font-normal opacity-70">· +${f.markup_applied.toFixed(2)}</span>
                                        )}
                                      </div>
                                    )}
                                    {/* Refundable badge — visible to everyone */}
                                    {f.fare_rules?.refundable === true && (
                                      <div
                                        data-testid={`flight-refundable-${f.id}`}
                                        title="This fare is refundable per the carrier's fare rules"
                                        className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border text-emerald-800 bg-emerald-50 border-emerald-200"
                                      >
                                        <RefreshCw className="w-2.5 h-2.5" /> Refundable
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="md:col-span-5 flex items-center gap-4">
                                  <div className="text-center">
                                    <div className="font-heading text-2xl font-semibold">{f.depart_time}</div>
                                    <div className="text-xs text-slate-500 font-mono">{f.from}</div>
                                  </div>
                                  <div className="flex-1 flex flex-col items-center text-xs text-slate-500">
                                    <Clock className="w-3.5 h-3.5 mb-1" /> {formatDur(f.duration_min)}
                                    <div className="w-full border-t border-dashed border-slate-300 my-1" />
                                    <div
                                      title={(() => {
                                        if (f.stops === 0) return "Direct flight — no layovers";
                                        // Build via list from segment data if available
                                        const via = [];
                                        if (Array.isArray(f.travelport_segments) && f.travelport_segments.length > 1) {
                                          f.travelport_segments.slice(0, -1).forEach((s) => via.push(s.Destination));
                                        } else if (f.amadeus_offer?.itineraries?.[0]?.segments?.length > 1) {
                                          f.amadeus_offer.itineraries[0].segments.slice(0, -1).forEach((s) => via.push(s.arrival?.iataCode));
                                        }
                                        return via.length ? `Layover at ${via.join(", ")}` : `${f.stops} stop${f.stops>1?"s":""}`;
                                      })()}
                                      className="cursor-help underline decoration-dotted underline-offset-2"
                                      data-testid={`flight-stops-${f.id}`}
                                    >
                                      {f.stops === 0 ? "Non-stop" : `${f.stops} stop${f.stops > 1 ? "s" : ""}`}
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-heading text-2xl font-semibold">{f.arrive_time}</div>
                                    <div className="text-xs text-slate-500 font-mono">{f.to}</div>
                                  </div>
                                </div>
                                <div className="md:col-span-2 text-center">
                                  <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]" data-testid={`flight-price-${f.id || ''}`}>
                                    {formatCcy(f.price + (isSel ? (sel.fare?.price_delta || 0) : 0), { decimals: 0 })}
                                  </div>
                                  <div className="text-xs text-slate-500">from · {f.seats_left} left</div>
                                  {user?.role === "admin" && f.native_price != null && f.native_currency && f.native_currency !== "USD" && (
                                    <div
                                      data-testid={`admin-native-price-${f.id || f.source_label}`}
                                      className="mt-0.5 text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block leading-tight"
                                      title={`Supplier API amount (admin only) · ${f.fx?.rate_source || ""}`}
                                    >
                                      API · {Number(f.native_price).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} {f.native_currency}
                                    </div>
                                  )}
                                  <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                                    {(() => {
                                      // Reconcile base/taxes against the actual displayed total so the
                                      // breakdown ALWAYS sums to the price shown on the card. Same logic
                                      // used in the View Fares modal — brand delta sits on BASE only,
                                      // taxes pass through.
                                      const cardTotal = Math.round((Number(f.price) + (isSel ? Number(sel.fare?.price_delta) || 0 : 0)) * 100) / 100;
                                      const selBase = isSel ? (Number(sel.fare?.base_price) || 0) : 0;
                                      const selTax  = isSel ? (Number(sel.fare?.taxes) || 0) : 0;
                                      const headBase = Number(f.base_price) || 0;
                                      const headTax  = Number(f.taxes) || 0;
                                      let base = selBase > 0 ? selBase : headBase;
                                      let taxes = selTax > 0 ? selTax : headTax;
                                      const sum = base + taxes;
                                      const tiesOut = sum > 0 && Math.abs(sum - cardTotal) < 0.5;
                                      if (!tiesOut) {
                                        if (taxes > 0 && taxes < cardTotal) {
                                          base = Math.round((cardTotal - taxes) * 100) / 100;
                                        } else if (base > 0 && base < cardTotal) {
                                          taxes = Math.round((cardTotal - base) * 100) / 100;
                                        } else {
                                          base = Math.round(cardTotal * 0.7 * 100) / 100;
                                          taxes = Math.round((cardTotal - base) * 100) / 100;
                                        }
                                      }
                                      return (
                                        <FareBreakdown
                                          base={base}
                                          taxes={taxes}
                                          markup={f.markup_applied || 0}
                                          pax={pax}
                                          currency={f.currency || "USD"}
                                          testId={`fare-breakdown-${f.id}`}
                                          showMarkup={user?.role === "admin"}
                                          compact
                                        />
                                      );
                                    })()}
                                  </div>
                                </div>
                                <div className="md:col-span-2 flex md:justify-end" onClick={(e) => e.stopPropagation()}>
                                  <Button data-testid={`flight-select-${f.id}`}
                                          onClick={() => openFare(idx, f)}
                                          className={isSel ? "bg-[var(--brand-accent)] hover:bg-[var(--brand-primary-dark)] rounded-full" : "bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"}>
                                    {isSel ? `${sel.fare.name} ✓` : "View fares"} <Plane className="w-4 h-4 ml-2" />
                                  </Button>
                                </div>
                              </div>
                              {isSel && (
                                <div className="mt-3 pt-3 border-t border-dashed border-teal-200 flex flex-wrap gap-3 text-xs text-slate-600">
                                  <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Cabin {sel.fare.cabin_baggage_kg}kg · Check-in {formatCheckedBaggage(sel.fare.checked_baggage_qty, sel.fare.checked_baggage_kg)}</span>
                                  {sel.fare.meal && <span className="inline-flex items-center gap-1.5"><Utensils className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Meal included</span>}
                                  {sel.fare.seat_selection && <span className="inline-flex items-center gap-1.5"><Armchair className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Free seat selection</span>}
                                  <span className="inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> {sel.fare.refundable ? "Refundable" : "Non-refundable"}</span>
                                </div>
                              )}

                              {/* Expand chevron at the bottom of the card */}
                              <div className="mt-2 text-center">
                                <button
                                  type="button"
                                  data-testid={`flight-expand-${f.id}`}
                                  onClick={(e) => { e.stopPropagation(); setExpanded((cur) => ({ ...cur, [f.id]: !cur[f.id] })); }}
                                  className="inline-flex items-center gap-1 text-[11px] text-[var(--brand-primary)] hover:text-[var(--brand-primary-dark)] font-medium"
                                >
                                  {expanded[f.id] ? <>Hide flight details <ChevronUp className="w-3 h-3" /></> : <>Flight details <ChevronDown className="w-3 h-3" /></>}
                                </button>
                              </div>

                              {/* Inline expandable segment timeline (Trip.com-style) */}
                              {expanded[f.id] && (
                                <div data-testid={`flight-details-${f.id}`} className="mt-3 pt-4 border-t border-slate-200">
                                  <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-3">Flight details</div>
                                  <FlightSegmentTimeline flight={f} />
                                </div>
                              )}
                            </Card>
                          );
                        })}
                      </div>
                    )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {results && allSelected && (
        <>
          {/* Spacer so the fixed bottom bar never overlaps the last result card */}
          <div aria-hidden className="h-28" />

          {/* Floating action bar — sits above the page on every viewport, clearly elevated */}
          <div
            role="dialog"
            aria-label="Continue with selected flight"
            className="fixed inset-x-0 bottom-0 z-40 pointer-events-none"
          >
            <div className="mx-auto max-w-5xl px-4 pb-4 sm:pb-6 pointer-events-auto">
              <div
                data-testid="flight-continue-bar"
                className="rounded-2xl bg-gradient-to-r from-[#0f3a37] via-[#0f766e] to-[var(--brand-primary-dark)] shadow-[0_20px_60px_-15px_rgba(15,118,110,0.6)] ring-1 ring-white/10 px-5 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-4"
              >
                <div className="text-white">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-semibold">
                    Total fare · {pax} pax
                  </div>
                  <div className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                    {formatCcy(total)} <span className="text-sm font-medium text-white/80">{dispCcy}</span>
                  </div>
                </div>
                <Button
                  data-testid="flight-continue-btn"
                  onClick={proceed}
                  className="bg-white hover:bg-slate-50 text-[var(--brand-primary-dark)] rounded-full h-12 px-7 text-base font-semibold shadow-md"
                >
                  Continue to travelers <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Brand Fare Sheet */}
      <Sheet open={!!activeFlight} onOpenChange={(o) => !o && setActiveFlight(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-auto">
          {activeFlight && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-2xl text-[var(--brand-accent)]">Choose your fare</SheetTitle>
                <SheetDescription>
                  {activeFlight.flight.airline_name} · {activeFlight.flight.flight_number} · <span className="font-mono">{activeFlight.flight.from} → {activeFlight.flight.to}</span> · {activeFlight.flight.depart_time} – {activeFlight.flight.arrive_time}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-3">
                {activeFlight.upsellLoading && (
                  <div data-testid="upsell-loading" className="text-xs text-slate-500 text-center py-2 inline-flex items-center justify-center gap-2 w-full">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading branded fares…
                  </div>
                )}

                {!activeFlight.upsellLoading && activeFlight.upsellFares && activeFlight.upsellFares.length > 0 && (
                  <div data-testid="upsell-source-amadeus" className="mb-2 flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-teal-700 font-bold">
                      {user?.role === "admin"
                        ? `${(activeFlight.flight.supplier_source || activeFlight.flight.source || "").toUpperCase() === "TRAVELPORT" ? "Travelport" : "Amadeus"} Branded Fares`
                        : "Branded Fares"}
                    </span>
                    <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">Live · {activeFlight.upsellFares.length} options</span>
                  </div>
                )}

                {!activeFlight.upsellLoading && (!activeFlight.upsellFares || activeFlight.upsellFares.length === 0) && (
                  <div data-testid="upsell-unavailable" className="mb-2 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-semibold text-amber-800">Branded fares not available for this flight</div>
                      <div className="text-[11px] text-amber-700 mt-0.5">
                        {activeFlight.upsellReason || "This carrier does not publish multiple brand fares for the selected route. Showing the standard fare."}
                      </div>
                    </div>
                  </div>
                )}

                {(activeFlight.upsellFares && activeFlight.upsellFares.length > 0
                  ? activeFlight.upsellFares.map((uf) => {
                      // Reconcile base/taxes so they ALWAYS sum to the brand's
                      // own total. Carriers (Amadeus upsell, some mock paths)
                      // sometimes copy the headline's base/taxes onto every
                      // brand tier, which makes a $453 brand still display
                      // $6.99/$87.36 = $94.35. That's wrong.
                      //
                      // Rule: brand upsell delta applies to BASE FARE only —
                      // taxes are statutory and pass through. So we keep
                      // supplier-provided taxes (if any) and shift the
                      // remainder to base. If the supplier doesn't give us
                      // base/taxes at all, fall back to a 70/30 split.
                      const ufPrice = Number(uf.price) || (Number(activeFlight.flight.price) + (Number(uf.price_delta) || 0));
                      let base = Number(uf.base_price) || 0;
                      let taxes = Number(uf.taxes) || 0;
                      const sum = base + taxes;
                      const tiesOut = sum > 0 && Math.abs(sum - ufPrice) < 0.5;
                      if (!tiesOut) {
                        // Prefer supplier taxes, derive base = total - taxes.
                        if (taxes > 0 && taxes < ufPrice) {
                          base = Math.round((ufPrice - taxes) * 100) / 100;
                        } else if (base > 0 && base < ufPrice) {
                          taxes = Math.round((ufPrice - base) * 100) / 100;
                        } else {
                          // Nothing useful from supplier — 70/30 estimate.
                          base = Math.round(ufPrice * 0.7 * 100) / 100;
                          taxes = Math.round((ufPrice - base) * 100) / 100;
                        }
                      }
                      return ({
                      code: uf.offer_id || uf.code || uf.class || uf.brand_raw || uf.fare_basis || "U",
                      name: uf.name || uf.brand || `Economy${uf.fare_class ? ` (${uf.fare_class})` : ""}`,
                      fare_basis_alternatives: uf.fare_basis_alternatives || [],
                      cabin: uf.cabin,
                      fare_class: uf.class,
                      fare_basis: uf.fare_basis,
                      currency: uf.currency,
                      price: ufPrice,
                      base_price: base,
                      taxes: taxes,
                      price_delta: Math.max(0, ufPrice - (Number(activeFlight.flight.price) || 0)),
                      cabin_baggage_kg: uf.cabin_baggage_kg,
                      checked_baggage_kg: uf.checked_baggage_kg,
                      checked_baggage_qty: uf.checked_baggage_qty,
                      meal: !!(uf.meal_included || uf.meal),
                      seat_selection: !!(uf.seat_selection_included || uf.seat_selection),
                      refundable: !!uf.refundable,
                      amenities: uf.amenities || [],
                      changeable_fee: uf.refundable ? 0 : 0,
                      _upsell_offer: uf.raw_offer,
                      // Travelport upsells already carry per-brand fare_rules; Amadeus upsells only
                      // have a refundable boolean — synthesise a fare_rules block so the dialog
                      // can render uniformly across suppliers.
                      fare_rules: uf.fare_rules || (activeFlight.flight.supplier_source === "amadeus" ? {
                        refundable: !!uf.refundable,
                        change: null, cancel: null, no_show: null,
                        latest_ticketing: activeFlight.flight.fare_rules?.latest_ticketing || null,
                        source: "amadeus-upsell",
                      } : null),
                    });})
                  : activeFlight.flight.brand_fares.map((bf) => {
                      // Reconcile base/taxes so they ALWAYS sum to the brand's
                      // own total (= headline price + delta). The Amadeus mapper
                      // copies the headline's base/taxes onto every brand_fare,
                      // so a $453 brand otherwise displays $6.99/$87.36 (the
                      // headline) which is misleading. Rule: brand delta goes
                      // onto BASE only — taxes pass through.
                      const headBase = Number(activeFlight.flight.base_price) || 0;
                      const headTaxes = Number(activeFlight.flight.taxes) || 0;
                      const headTotal = Number(activeFlight.flight.price) || (headBase + headTaxes);
                      const delta = Number(bf.price_delta) || 0;
                      const brandTotal = headTotal + delta;
                      let base = Number(bf.base_price) || 0;
                      let taxes = Number(bf.taxes) || 0;
                      const sum = base + taxes;
                      const tiesOut = sum > 0 && Math.abs(sum - brandTotal) < 0.5;
                      if (!tiesOut) {
                        if (headTaxes > 0 && headTaxes < brandTotal) {
                          // Add delta to headline base; preserve headline taxes.
                          base = Math.round((brandTotal - headTaxes) * 100) / 100;
                          taxes = Math.round(headTaxes * 100) / 100;
                        } else if (headBase > 0 || headTaxes > 0) {
                          // Fallback to ratio scaling against the headline.
                          const hSum = headBase + headTaxes;
                          base = Math.round((brandTotal * headBase / hSum) * 100) / 100;
                          taxes = Math.round((brandTotal * headTaxes / hSum) * 100) / 100;
                        } else {
                          // 70/30 split when headline has no breakdown either.
                          base = Math.round(brandTotal * 0.7 * 100) / 100;
                          taxes = Math.round((brandTotal - base) * 100) / 100;
                        }
                      }
                      return {
                        ...bf,
                        base_price: Math.round(base * 100) / 100,
                        taxes: Math.round(taxes * 100) / 100,
                        checked_baggage_qty: bf.checked_baggage_qty,
                        checked_baggage_kg: bf.checked_baggage_kg,
                      };
                    })
                ).reduce((acc, bf) => {
                  // Final dedup: collapse cards with identical brand-tier + price + bag
                  // + meal + seat + refundable into a single row. INCLUDES `name` so
                  // different tiers (e.g. "Economy Saver" vs "Economy Flex") that
                  // happen to have the same price+amenities don't merge — the customer
                  // sees the tier variety carriers actually publish.
                  const total = Math.round((Number(activeFlight.flight.price) + (Number(bf.price_delta) || 0)) * 100) / 100;
                  const key = [
                    (bf.name || bf.brand_tier || "").toLowerCase(),
                    total,
                    bf.checked_baggage_kg || 0, bf.checked_baggage_qty || 0,
                    bf.cabin_baggage_kg || 0,
                    !!bf.refundable, !!bf.meal, !!bf.seat_selection,
                  ].join("|");
                  const dup = acc.find((x) => x._dedup_key === key);
                  if (dup) {
                    const alt = dup.fare_basis_alternatives || [];
                    if (bf.fare_basis && bf.fare_basis !== dup.fare_basis && !alt.includes(bf.fare_basis)) {
                      alt.push(bf.fare_basis);
                    }
                    dup.fare_basis_alternatives = alt;
                    return acc;
                  }
                  acc.push({ ...bf, _dedup_key: key });
                  return acc;
                }, []).map((bf, bfIdx) => {
                  const total = (activeFlight.flight.price + (bf.price_delta || 0)) * pax;
                  const isUp = !!bf._upsell_offer;
                  return (
                    <Card key={`${bf.code}-${bfIdx}`} data-testid={`brand-fare-${bf.code}`}
                          className="p-4 hover:shadow-md transition-all border-l-4 border-l-transparent hover:border-l-[var(--brand-primary)]">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-heading text-lg font-semibold text-[var(--brand-accent)] flex items-center gap-2">
                            {bf.name}
                            {bf.cabin && (
                              <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{bf.cabin}</span>
                            )}
                            {isUp && bf.fare_class && (
                              <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded font-mono">Class {bf.fare_class}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {bf.refundable ? "Refundable" : "Non-refundable"}
                            {bf.fare_basis ? <> · <span className="font-mono">{bf.fare_basis}</span></> : null}
                            {(bf.fare_basis_alternatives && bf.fare_basis_alternatives.length > 0) && (
                              <>
                                {" "}
                                <span
                                  className="ml-1 inline-flex items-center bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-mono px-1.5 py-0.5 rounded-full"
                                  title={`Multiple fare basis under this RBD: ${[bf.fare_basis, ...bf.fare_basis_alternatives].join(", ")}`}
                                >
                                  +{bf.fare_basis_alternatives.length} more
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-heading text-xl font-bold text-[var(--brand-accent)]">
                            {formatCcy(activeFlight.flight.price + (bf.price_delta || 0), { decimals: 0 })}
                          </div>
                          <div className="text-[11px] text-slate-500">per pax · total {formatCcy(total, { decimals: 0 })}</div>
                          <div className="mt-1 flex justify-end">
                            <FareBreakdown
                              base={bf.base_price || 0}
                              taxes={bf.taxes || 0}
                              pax={pax}
                              currency={bf.currency || "USD"}
                              testId={`brand-fare-breakdown-${bf.code}`}
                              compact
                              label="Fare details"
                            />
                          </div>
                        </div>
                      </div>
                      <ul className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                        <li className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Cabin: {bf.cabin_baggage_kg || 7}kg</li>
                        <li className="flex items-center gap-1.5">
                          <Briefcase className="w-3.5 h-3.5 text-[var(--brand-primary)]" />
                          Check-in: {formatCheckedBaggage(bf.checked_baggage_qty, bf.checked_baggage_kg)}
                        </li>
                        <li className="flex items-center gap-1.5">
                          {bf.meal
                            ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Meal included</>
                            : <><span className="w-3.5 h-3.5 text-slate-300 inline-flex items-center justify-center">✕</span> No meal</>}
                        </li>
                        <li className="flex items-center gap-1.5">
                          {bf.seat_selection
                            ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Seat selection</>
                            : <><span className="w-3.5 h-3.5 text-slate-300 inline-flex items-center justify-center">✕</span> No seat selection</>}
                        </li>
                      </ul>
                      {bf.amenities && bf.amenities.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {bf.amenities.slice(0, 6).map((a, i) => (
                            <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{a}</span>
                          ))}
                        </div>
                      )}
                      <Button data-testid={`brand-fare-select-${bf.code}`} onClick={() => confirmFare(bf)}
                              className="w-full mt-3 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
                        Select {bf.name}
                      </Button>
                      <button
                        type="button"
                        data-testid={`brand-fare-rules-${bf.code}`}
                        onClick={() => setFareRulesFor({
                          flight: { ...activeFlight.flight, fare_rules: bf.fare_rules || activeFlight.flight.fare_rules },
                          label: `${bf.name} · ${activeFlight.flight.from} → ${activeFlight.flight.to}`,
                        })}
                        className="w-full mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[var(--brand-primary)] hover:text-[#0f766e] hover:underline underline-offset-2 transition-colors py-1"
                      >
                        <FileText className="w-3.5 h-3.5" /> Fare rules
                      </button>
                    </Card>
                  );
                })}
                <div className="text-xs text-slate-500 flex items-start gap-1.5 mt-3">
                  <ShieldCheck className="w-3.5 h-3.5 text-[var(--brand-primary)] mt-0.5 flex-shrink-0" />
                  Baggage allowance shown is per passenger. Brand fares are provided by the operating carrier.
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <PromoStrip />

      {fareRulesFor && (
        <FareRulesDialog
          open={true}
          onOpenChange={(o) => { if (!o) setFareRulesFor(null); }}
          flight={fareRulesFor.flight}
          segmentLabel={fareRulesFor.label}
        />
      )}
    </div>
  );
}
